if (!customElements.get('product-media')) {
  class ProductMedia extends HTMLElement {
    constructor() {
      super()
      this.enableClickZoom = this.hasAttribute('zoom-on-click')
      this.enablePinchZoom = this.hasAttribute('zoom-on-touch')

      if (this.enableClickZoom) this.initClickZoom()
      if (this.enablePinchZoom) this.initPinchZoom()
    }

    initClickZoom() {
      const url = this.getAttribute('data-drift-url')
      if (!url) return

      window.driftPromise =
        window.driftPromise ||
        new Promise((resolve) => {
          const script = document.createElement('script')
          script.src = url
          script.async = true
          script.onload = resolve
          document.head.appendChild(script)
        })

      window.driftPromise.then(() => {
        const images = this.querySelectorAll('.product-media__image')
        images.forEach((image) => {
          const paneContainer = image.closest('.media-container')
          if (paneContainer) {
            new window.Drift(image, {
              zoomFactor: 2,
              paneContainer: paneContainer,
              inlinePane: 100,
              touchDelay: 500,
            })
          }
        })

        // 'zoom-active' class on click
        const imageContainers = this.querySelectorAll('.media-container.type-image')
        imageContainers.forEach((el) => {
          el.addEventListener('click', () => {
            const touchDevice = matchMedia('(hover: none)').matches
            if (!touchDevice) el.classList.toggle('zoom-active')
          })
        })
      })
    }

    initPinchZoom() {
      const url = this.getAttribute('data-pinch-url')
      if (!url) return

      window.pinchPromise =
        window.pinchPromise ||
        new Promise((resolve) => {
          const script = document.createElement('script')
          script.src = url
          script.async = true
          script.onload = resolve
          document.head.appendChild(script)
        })

      window.pinchPromise.then(() => {
        const wrappers = this.querySelectorAll('.pinch-zoom')
        wrappers.forEach((el) => {
          new window.PinchZoom(el, {
            minZoom: 0.9,
            draggableUnzoomed: false,
          })
        })
      })
    }
  }

  customElements.define('product-media', ProductMedia)
}

if (!customElements.get('product-slider')) {
  class ProductSlider extends HTMLElement {
    constructor() {
      super()
      this.viewer = this.querySelector('.slider__viewer')
      this.thumbnails = this.querySelector('.slider__thumbnails')
      this.thumbnailsDirection = this.querySelector('.slider__thumbnails-container')?.getAttribute('direction')
      this.buttonPrev = this.querySelector('.swiper-button-prev')
      this.buttonNext = this.querySelector('.swiper-button-next')
      this.isRTL = document.documentElement.dir === 'rtl' ? true : false
      this.viewerSlider = null
      this.thumbnailsSlider = null

      this.hasModels = this.viewer.querySelectorAll('.media-container.type-model').length > 0
      this.hasVideos =
        this.viewer.querySelectorAll('.media-container.type-video, .media-container.type-external_video').length > 0

      if (this.hasModels) this.xrBtn = this.querySelector('.xr-btn')

      this.createSlider()
    }

    createSlider() {
      if (this.thumbnails) this.createThumbnailsSlider()
      this.createViewerSlider()

      if (this.hasModels) {
        this.viewerSlider.on('slideChange', () => this.pauseSliderModels())
        this.viewerSlider.on('slideChangeTransitionEnd', () => this.handleModelViewers())
      }

      if (this.hasVideos) {
        this.viewerSlider.on('slideChange', () => this.pauseSliderVideos())
      }
    }

    createThumbnailsSlider() {
      const thumbnailsSliderOptions = {
        slidesPerView: 'auto',
        spaceBetween: 4,
        direction: 'horizontal',
        mousewheel: true,
        watchSlidesProgress: true,
        breakpoints: {
          750: {
            slidesPerView: 'auto',
            spaceBetween: 12,
            direction: this.thumbnailsDirection,
          },
        },
      }

      this.thumbnailsSlider = new Swiper(this.thumbnails, thumbnailsSliderOptions)

      if (this.isRTL) {
        this.changeLanguageDirection()
        this.thumbnailsSlider.on('resize', () => this.changeLanguageDirection())
      }
    }

    changeLanguageDirection() {
      if (window.innerWidth >= 750 && this.thumbnailsDirection === 'vertical') {
        this.thumbnailsSlider.changeLanguageDirection('ltr')
      } else {
        this.thumbnailsSlider.changeLanguageDirection('rtl')
      }
    }

    createViewerSlider() {
      const viewerSliderOptions = {
        spaceBetween: 4,
        autoHeight: true,
        simulateTouch: false,
        breakpoints: {
          750: {
            spaceBetween: 12,
          },
        },
        thumbs: {
          swiper: this.thumbnails ? this.thumbnailsSlider : '',
        },
        navigation: {
          prevEl: this.buttonPrev,
          nextEl: this.buttonNext,
          addIcons: false,
        },
        a11y: {
          prevSlideMessage: this.buttonPrev?.dataset.label,
          nextSlideMessage: this.buttonNext?.dataset.label,
        },
      }

      this.viewerSlider = new Swiper(this.viewer, viewerSliderOptions)

      if (this.viewerSlider.slides.length > 1) this.unlockNavigationButtons()
    }

    unlockNavigationButtons() {
      this.buttonPrev?.classList.remove('hidden')
      this.buttonNext?.classList.remove('hidden')
    }

    disableTouchMove() {
      this.viewerSlider.allowTouchMove = false
    }

    enableTouchMove() {
      this.viewerSlider.allowTouchMove = true
    }

    handleModelViewers() {
      this.updateXRButton()
      this.pauseSliderModels()
    }

    pauseSliderModels() {
      const productModels = this.viewer.querySelectorAll('.product-model')
      productModels?.forEach((model) => model.modelViewerUI?.pause())
      if (!this.viewerSlider.allowTouchMove) this.enableTouchMove()
    }

    updateXRButton() {
      const activeSlide = this.viewerSlider.slides[this.viewerSlider.activeIndex]
      const productModel = activeSlide.querySelector('.product-model')

      if (productModel) {
        const modelId = productModel.dataset.modelId
        this.xrBtn.setAttribute('data-shopify-model3d-id', modelId)
      } else {
        const firstModelId = this.xrBtn.dataset.firstModelId
        this.xrBtn.setAttribute('data-shopify-model3d-id', firstModelId)
      }
    }

    pauseSliderVideos() {
      this.viewerSlider.slides.forEach((slide) => {
        const video = slide.querySelector('video, iframe')

        if (video) {
          const VideoMedia = video.closest('video-media')
          const autoLoad = VideoMedia?.hasAttribute('auto-load')

          if (!autoLoad) {
            VideoMedia.pauseVideo()
          }
        }
      })
    }
  }

  customElements.define('product-slider', ProductSlider)
}

if (!customElements.get('product-model')) {
  class ProductModel extends HTMLElement {
    constructor() {
      super()
      this.poster = this.querySelector('.product-model__poster')

      this.poster?.addEventListener('click', this.loadContent.bind(this))
    }

    loadContent() {
      if (this.getAttribute('loaded')) return

      const content = this.querySelector('template').content.firstElementChild.cloneNode(true)
      this.appendChild(content)

      this.setAttribute('loaded', true)

      Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this.setupModelViewerUI.bind(this),
        },
      ])
    }

    setupModelViewerUI(errors) {
      if (errors) return
      this.modelViewerUI = new Shopify.ModelViewerUI(this.querySelector('model-viewer'))

      this.updateSliderInteraction()
      this.addEventListener('click', this.updateSliderInteraction.bind(this))
      this.addEventListener('touchend', this.updateSliderInteraction.bind(this))
      this.pauseSiblingModels()
    }

    updateSliderInteraction() {
      const modelViewer = this.querySelector('.shopify-model-viewer-ui model-viewer')
      const productSlider = this.closest('product-slider')
      if (!productSlider) return
      const isDisabled = modelViewer?.classList.contains('shopify-model-viewer-ui__disabled') ?? false

      if (isDisabled) productSlider.enableTouchMove()
      else productSlider.disableTouchMove()
    }

    pauseSiblingModels() {
      const productModels = this.closest('.product-media__grid')?.querySelectorAll('.product-model')

      productModels?.forEach((model) => {
        if (this.modelViewerUI === model.modelViewerUI) return
        model.modelViewerUI?.pause()
      })
    }
  }

  customElements.define('product-model', ProductModel)
}

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super()
      this.productView = this.closest('[data-product-view]')
      this.section = this.closest('.shopify-section')
      this.selectSize = this.hasAttribute('select-size')
      if (this.selectSize) {
        this.sizeOption = this.dataset.sizeOption
        this.isSizeSelected = false
      }

      this.addEventListener('change', this.onVariantChange)
    }

    onVariantChange(e) {
      const target = e.target
      this.updateSelectedOption(target)
      this.fetchUpdatedSection(target)
    }

    updateSelectedOption(target) {
      if (target.tagName !== 'SELECT') return

      const options = Array.from(target.options)
      const selectedIndex = target.selectedIndex

      options.forEach((option, index) => {
        if (index === selectedIndex) {
          option.setAttribute('selected', 'selected')
        } else {
          option.removeAttribute('selected')
        }
      })
    }

    fetchUpdatedSection(target) {
      const selectedValues = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
        ({ dataset }) => dataset.valueId
      )
      const params = selectedValues.length > 0 ? `&option_values=${selectedValues.join(',')}` : ''
      const url = `${this.dataset.productUrl}?section_id=${this.dataset.sectionId}${params}`

      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html')

          this.replaceVariantPicker(html, target)

          this.updateProductInfo(html)

          this.updateHistoryState()

          this.updateVariantInput()

          if (document.body.classList.contains('user-is-tabbing')) {
            document.querySelector(`#${target.id}`).focus()
          }
        })
        .catch((error) => {
          console.error(error)
        })
    }

    replaceVariantPicker(html, target) {
      const selector = '[data-variant-picker]'
      const currentPickers = this.productView.querySelectorAll(selector)
      const incomingPickers = html.querySelectorAll(selector)

      currentPickers.forEach((currentPicker, index) => {
        const incomingPicker = incomingPickers[index]
        if (currentPicker && incomingPicker) {
          currentPicker.innerHTML = incomingPicker.innerHTML

          // Update attributes
          Array.from(incomingPicker.attributes).forEach((attr) => {
            currentPicker.setAttribute(attr.name, attr.value)
          })

          // Validate select a size
          if (currentPicker.selectSize) {
            currentPicker.validateSelectSize(target)
          }
        }
      })
    }

    validateSelectSize(target) {
      const targetOption = target.dataset.option

      if (targetOption === this.sizeOption) {
        this.isSizeSelected = true
      }

      if (this.isSizeSelected) {
        this.updateSelectedSize()
      }
    }

    updateSelectedSize() {
      const checkedElement = this.querySelector('[data-checked]')
      const tagName = checkedElement.tagName

      if (tagName === 'INPUT') {
        checkedElement.setAttribute('checked', 'checked')
      } else if (tagName === 'OPTION') {
        const options = Array.from(checkedElement.closest('select').options)
        const selectedIndex = options.indexOf(checkedElement)

        options.forEach((option, index) => {
          if (index === selectedIndex) {
            option.setAttribute('selected', 'selected')
          } else {
            option.removeAttribute('selected')
          }
        })
      }
    }

    updateHistoryState() {
      const isProductPage = window.location.pathname.includes('/products/')

      if (!isProductPage) return

      const url = new URL(window.location.href)
      const variantId = this.dataset.variantId

      if (variantId) {
        url.searchParams.set('variant', variantId)
      } else {
        url.searchParams.delete('variant')
      }

      window.history.replaceState({}, '', url.toString())
    }

    updateProductInfo(html) {
      const hasValidSize = !this.selectSize || this.isSizeSelected

      const replaceContent = (selector, { container = this.productView, callback } = {}) => {
        if (!container) return
        const targets = container.querySelectorAll(selector)
        const sources = html.querySelectorAll(selector)

        targets.forEach((target, index) => {
          const source = sources[index]
          if (target && source) {
            target.innerHTML = source.innerHTML
            callback?.(target)
          }
        })
      }

      // Price
      replaceContent('[data-price]')

      // Media
      replaceContent('[data-media]', {
        callback: (targetMedia) => {
          if (targetMedia.enableClickZoom) targetMedia.initClickZoom()
          if (targetMedia.enablePinchZoom) targetMedia.initPinchZoom()
        },
      })

      // Buy buttons
      replaceContent('[data-buy-buttons]', {
        callback: (targetBuyBtns) => {
          targetBuyBtns.classList.toggle('select-size', !hasValidSize)
        },
      })

      // Back in stock modal
      const backInStockModalSelector = `#${this.productView.querySelector('[data-bis-modal]')?.id}`
      replaceContent(backInStockModalSelector, {
        container: document.body,
      })

      // Inventory
      replaceContent('[data-inventory]', {
        callback: (targetInventory) => {
          targetInventory.classList.toggle('select-size', !hasValidSize)
        },
      })

      // SKU / Barcode
      replaceContent('[data-sku-barcode]')

      // Pickup availability
      replaceContent('[data-pickup-availability]')

      // Model info
      replaceContent('[data-model-info]')

      // Badges
      replaceContent('[data-badges]')

      // Sticky ATC card
      replaceContent('[data-sticky-atc-card]', {
        container: this.section,
      })

      // View details link
      replaceContent('[data-view-details-link]')
    }

    updateVariantInput() {
      const variantId = this.dataset.variantId
      this.forms = this.forms || this.productView.querySelectorAll('.js-product-form, .js-installment-form')

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]')
        input.value = variantId
        input.dispatchEvent(new Event('change', { bubbles: true }))
      })
    }
  }

  customElements.define('variant-picker', VariantPicker)
}

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super()
      this.form = this.querySelector('form')
      this.cartDrawer = document.querySelector('cart-drawer')

      this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
    }

    onSubmitHandler(e) {
      e.preventDefault()
      const submitButton = this.querySelector('[type="submit"]')

      if (submitButton.getAttribute('aria-disabled') === 'true') return

      this.hideError()

      submitButton.setAttribute('aria-disabled', 'true')
      submitButton.classList.add('loading-spinner')

      const formData = new FormData(this.form)
      const cartSections = getCartSections()

      formData.append('sections', cartSections)
      formData.append('sections_url', window.location.pathname)

      const requestOptions = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      }

      fetch(`${Theme.routes.cart_add_url}`, requestOptions)
        .then((response) => response.json())
        .then(async (cartData) => {
          // Case 1. If error
          if (cartData.status) {
            this.handleError(cartData.description)

            if (!cartData.sections) {
              try {
                cartData.sections = await this.fetchRequestedSections()
              } catch (sectionError) {
                console.error('Failed to fetch sections:', sectionError)
              }
            }

            this.dispatchEvent(
              new CustomEvent('cart:error', {
                bubbles: true,
                detail: {
                  cartData: cartData,
                },
              })
            )
            return
          }

          // Case 2. If success and there's no cart drawer
          if (!this.cartDrawer) {
            window.location = Theme.routes.cart_url
            return
          }

          // Case 3. If success and there's cart drawer
          if (this.cartDrawer) {
            const eventDetail = {
              cartData: cartData,
              trigger: submitButton,
            }

            const quickBuyModal = this.closest('#quick-buy-modal')
            if (quickBuyModal) {
              eventDetail.trigger = quickBuyModal.triggerElement
              quickBuyModal.closeDialog()
            }

            this.dispatchEvent(
              new CustomEvent('cart:add', {
                bubbles: true,
                detail: eventDetail,
              })
            )
          }
        })
        .catch((error) => {
          console.error('Error:', error)
        })
        .finally(() => {
          submitButton.removeAttribute('aria-disabled')
          submitButton.classList.remove('loading-spinner')
        })
    }

    async fetchRequestedSections() {
      const sections = getCartSections()
      const response = await fetch(`${window.location.pathname}?sections=${sections}`)
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
      const parsedData = await response.json()
      return parsedData
    }

    hideError() {
      const errorElement = this.querySelector('#product-form-error')
      if (errorElement) errorElement.textContent = ''
    }

    handleError(errorData) {
      const errorElement = this.querySelector('#product-form-error')
      let errorMessage = ''

      if (typeof errorData === 'string') {
        errorMessage = errorData
      } else if (Array.isArray(errorData)) {
        errorMessage = errorData
          .map((err) => {
            if (typeof err === 'object' && err !== null) {
              return err.message || err.description || JSON.stringify(err)
            }
            return err
          })
          .join(', ')
      } else if (typeof errorData === 'object' && errorData !== null) {
        if (errorData.description) {
          errorMessage = errorData.description
        } else if (errorData.message) {
          errorMessage = errorData.message
        } else {
          errorMessage = Object.values(errorData)
            .map((val) => (Array.isArray(val) ? val.join(', ') : val))
            .join(', ')
        }
      }

      if (errorElement && errorMessage) {
        errorElement.textContent = errorMessage
      }
    }
  }

  customElements.define('product-form', ProductForm)
}

if (!customElements.get('back-in-stock-form')) {
  class BackInStockForm extends HTMLElement {
    constructor() {
      super()
      this.dialog = this.closest('.dialog-component')
      this.formMessage = this.querySelector('[data-form-message]')

      if (this.formMessage) this.dialog.openDialog()
    }
  }

  customElements.define('back-in-stock-form', BackInStockForm)
}
